import {
  WsmDealerCustomerAssignInput,
  WsmDealerCustomerFragment,
  WsmDealerCustomerUpdateInput,
  WsmDealerGroupCreateInput,
  WsmDealerGroupFragment,
  WsmDealerGroupUpdateInput,
  WsmTierPriceBulkCreateInput,
  WsmTierPriceFragment,
  WsmTierPriceUpdateInput,
} from "@dashboard/graphql";
import { getFullName } from "@dashboard/misc";
import { parseWsmAmount } from "@dashboard/wsmShared/money";

import { ParsedPasteRow } from "./paste";
import {
  WsmDealerCustomerFormData,
  WsmDealerGroupFormData,
  WsmTierRowDraft,
} from "./types";

let draftKeySeq = 0;

/** A handle unique within one form, and so within the one save it submits. */
export const makeDraftKey = (prefix: string) => `${prefix}-${++draftKeySeq}`;

export const emptyGroupFormData = (): WsmDealerGroupFormData => ({
  code: "",
  name: "",
  rows: [],
});

export const groupToFormData = (
  group: WsmDealerGroupFragment,
  tiers: WsmTierPriceFragment[],
): WsmDealerGroupFormData => ({
  code: group.code,
  name: group.name,
  rows: tiers.map(tierToDraft),
});

/**
 * What the grid DRAWS for an amount. The value itself is left exactly as the API
 * handed it back ("270.000"), because that string is what the cell editor opens
 * on and what the save path sends; only the drawn text is money-shaped.
 *
 * A canvas Datagrid cell needs a STRING, and wsmShared/money only returns a
 * React element or a view object, so the parse rule is shared and the final
 * formatting is Intl here. A value the currency cannot hold ("119.995" in USD)
 * is drawn whole rather than rounded: the merchant seeing the odd number and the
 * server refusing it beats us quietly turning their price into a different one.
 */
export const formatTierAmountDisplay = (amount: string, currencyCode: string | null): string => {
  const parsed = parseWsmAmount(amount.trim());

  if (parsed === null) {
    return amount;
  }

  if (!currencyCode) {
    return parsed.toFixed(2);
  }

  try {
    const drawn = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
    }).format(parsed);

    return Number(drawn.replace(/[^0-9.-]/g, "")) === parsed ? drawn : amount.trim();
  } catch {
    return parsed.toFixed(2);
  }
};

/**
 * What actually goes on the wire for an amount. The server refuses more than two
 * decimals, including the "270.000" it handed us a moment earlier, so a merchant
 * who edits only the quantity on an existing row would be refused over a price
 * they never typed. Trailing zeros are dropped because that is lossless; a value
 * that genuinely needs a third decimal ("119.995") is sent untouched so the
 * server refuses it rather than us quietly turning it into a different price.
 *
 * The real fix is server-side, comparing the VALUE rather than the string
 * length. This stays until that lands, and is harmless afterwards.
 */
export const tierAmountForSave = (amount: string): string => {
  const raw = amount.trim();
  const parsed = parseWsmAmount(raw);

  if (parsed === null) {
    return raw;
  }

  const twoDecimals = parsed.toFixed(2);

  return Number(twoDecimals) === parsed ? twoDecimals : raw;
};

export const tierToDraft = (tier: WsmTierPriceFragment): WsmTierRowDraft => ({
  key: makeDraftKey("tier"),
  id: tier.id,
  variantId: tier.variant.id,
  sku: tier.variant.sku ?? "",
  variantName: tier.variant.name,
  productName: tier.variant.product.name,
  minQuantity: tier.minQuantity,
  // Verbatim: the editor and the save path both see what the API sent.
  amount: String(tier.amount ?? ""),
});

export const getGroupCreateInput = (data: WsmDealerGroupFormData): WsmDealerGroupCreateInput => ({
  code: data.code.trim(),
  name: data.name.trim(),
});

export const getGroupUpdateInput = (data: WsmDealerGroupFormData): WsmDealerGroupUpdateInput =>
  getGroupCreateInput(data);

/**
 * Everything one save of the tier grid has to send. The contract gives no
 * single "replace the group's prices" mutation, so the grid is diffed against
 * what the server handed back: rows added in this save go out in ONE
 * bulkCreate (a 300-row paste is one call, not 300), an edited row goes out as
 * an update, and a row taken off the grid goes out in one bulkDelete.
 */
export interface WsmTierChanges {
  creates: WsmTierPriceBulkCreateInput[];
  /** Row index in the draft grid for each entry of `creates`, in the same order. */
  createRowIndexes: number[];
  updates: Array<{ id: string; input: WsmTierPriceUpdateInput }>;
  deleteIds: string[];
}

const sameRow = (a: WsmTierRowDraft, b: WsmTierRowDraft) =>
  a.minQuantity === b.minQuantity && a.amount.trim() === b.amount.trim();

/** A tier price is unique on (variant, quantity break) - that is DUPLICATE_TIER_BREAK. */
const slotOf = (row: { variantId: string; minQuantity: number }) =>
  `${row.variantId}:${row.minQuantity}`;

/**
 * A save is diffed against what the server last handed back, and it has to be
 * REPEATABLE: the grid keeps its draft rows across a save, so a row this save
 * created still carries `id: null` the next time the merchant hits save. Matching
 * such a row to the server row that now occupies its (variant, break) slot is
 * what stops the second save from deleting the real rows and making them again,
 * and what lets a half-refused save be fixed and re-sent rather than earning
 * DUPLICATE_TIER_BREAK on the rows that did go through.
 */
export const diffTierRows = (
  original: WsmTierRowDraft[],
  draft: WsmTierRowDraft[],
  groupId: string,
): WsmTierChanges => {
  const originalById = new Map(original.filter(row => row.id).map(row => [row.id, row]));
  const originalBySlot = new Map(original.filter(row => row.id).map(row => [slotOf(row), row]));
  /** Server rows already accounted for by a draft row, so they are not deleted. */
  const keptIds = new Set<string>();
  const changes: WsmTierChanges = {
    creates: [],
    createRowIndexes: [],
    updates: [],
    deleteIds: [],
  };

  draft.forEach((row, index) => {
    // An id-less row is not automatically new: the server may already hold this
    // exact slot from an earlier save of this same grid.
    const existing = row.id ? originalById.get(row.id) : originalBySlot.get(slotOf(row));

    if (!existing?.id || keptIds.has(existing.id)) {
      changes.creates.push({
        variant: row.variantId,
        group: groupId,
        minQuantity: row.minQuantity,
        amount: tierAmountForSave(row.amount),
      });
      changes.createRowIndexes.push(index);

      return;
    }

    keptIds.add(existing.id);

    if (!sameRow(existing, row)) {
      changes.updates.push({
        id: existing.id,
        input: { minQuantity: row.minQuantity, amount: tierAmountForSave(row.amount) },
      });
    }
  });

  changes.deleteIds = original
    .filter(row => row.id && !keptIds.has(row.id))
    .map(row => row.id as string);

  return changes;
};

/**
 * Deletes that have to go FIRST because a create in the same save wants the slot
 * they hold; every other delete goes last, so a refused create can never be
 * paid for with rows that are already gone.
 */
export const splitDeletesByBlocking = (
  original: WsmTierRowDraft[],
  changes: WsmTierChanges,
): { blocking: string[]; trailing: string[] } => {
  const wantedSlots = new Set(
    changes.creates.map(create => `${create.variant}:${create.minQuantity}`),
  );
  const slotById = new Map(original.filter(row => row.id).map(row => [row.id as string, slotOf(row)]));
  const blocking: string[] = [];
  const trailing: string[] = [];

  changes.deleteIds.forEach(id => {
    const slot = slotById.get(id);

    if (slot && wantedSlots.has(slot)) {
      blocking.push(id);
    } else {
      trailing.push(id);
    }
  });

  return { blocking, trailing };
};

export interface ResolvedPaste {
  rows: WsmTierRowDraft[];
  /** Lines whose SKU no variant carries. Named, never dropped silently. */
  unknown: ParsedPasteRow[];
  /**
   * Draft keys of the grid rows these pasted rows stand in for. Keys, not ids:
   * an unsaved row has no id yet and still must not be doubled.
   */
  replacedKeys: Set<string>;
}

/**
 * Turns parsed paste lines into grid rows, once the SKUs have been resolved to
 * variants. A SKU already on the grid at the same quantity break REPLACES that
 * row rather than adding a second one: a duplicate is exactly what
 * DUPLICATE_TIER_BREAK refuses, and re-pasting a corrected price list is the
 * common case.
 *
 * The same rule applies WITHIN one paste. A price list that names a SKU twice
 * used to produce two rows at the same break and earn the very refusal this
 * function exists to prevent, so the last line for a SKU wins and replaces the
 * earlier one in place - the same "a re-paste corrects a price" rule, applied to
 * a list that corrects itself.
 */
export const resolvePasteRows = (
  parsed: ParsedPasteRow[],
  variantsBySku: Map<string, { id: string; name: string; sku: string; productName: string }>,
  minQuantity: number,
  existing: WsmTierRowDraft[],
): ResolvedPaste => {
  const rows: WsmTierRowDraft[] = [];
  const unknown: ParsedPasteRow[] = [];
  const replacedKeys = new Set<string>();
  /** Where in `rows` this paste already placed a line for a given variant. */
  const placedByVariant = new Map<string, number>();

  parsed.forEach(row => {
    const variant = variantsBySku.get(row.sku);

    if (!variant) {
      unknown.push(row);

      return;
    }

    const replaces = existing.find(
      current => current.variantId === variant.id && current.minQuantity === minQuantity,
    );

    if (replaces) {
      replacedKeys.add(replaces.key);
    }

    const placedAt = placedByVariant.get(variant.id);
    const resolvedRow: WsmTierRowDraft = {
      // The replaced row's key is reused so a second paste of the same list
      // resolves against this row too, rather than stacking a third copy.
      key: replaces?.key ?? makeDraftKey("tier"),
      id: replaces?.id ?? null,
      variantId: variant.id,
      sku: variant.sku,
      variantName: variant.name,
      productName: variant.productName,
      minQuantity,
      amount: row.amount,
    };

    if (placedAt !== undefined) {
      rows[placedAt] = resolvedRow;

      return;
    }

    placedByVariant.set(variant.id, rows.length);
    rows.push(resolvedRow);
  });

  return { rows, unknown, replacedKeys };
};

/**
 * Pasted rows land on top of the rows they replace, the rest keep their order.
 * Replacement is by draft key, so an unsaved row (no id yet) is replaced rather
 * than left behind as a duplicate of the row that supersedes it.
 */
export const mergePastedRows = (
  existing: WsmTierRowDraft[],
  pasted: WsmTierRowDraft[],
  replacedKeys: ReadonlySet<string> = new Set(pasted.map(row => row.key)),
) => [...existing.filter(row => !replacedKeys.has(row.key)), ...pasted];

/**
 * Saleor's own pagination cap is 100 rows a call, so a 300-line paste resolves
 * its SKUs in three calls rather than three hundred.
 */
export const SKU_LOOKUP_CHUNK = 100;

export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
};

export const getCustomerLabel = (user: {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}) => getFullName({ firstName: user.firstName ?? "", lastName: user.lastName ?? "" }) || user.email;

export const emptyCustomerFormData = (): WsmDealerCustomerFormData => ({
  userId: "",
  userLabel: "",
  groupId: "",
  taxExempt: false,
});

export const customerToFormData = (
  customer: WsmDealerCustomerFragment,
): WsmDealerCustomerFormData => ({
  userId: customer.user.id,
  userLabel: getCustomerLabel(customer.user),
  groupId: customer.group.id,
  taxExempt: customer.taxExempt,
});

export const getCustomerAssignInput = (
  data: WsmDealerCustomerFormData,
): WsmDealerCustomerAssignInput => ({
  user: data.userId,
  group: data.groupId,
  taxExempt: data.taxExempt,
});

export const getCustomerUpdateInput = (
  data: WsmDealerCustomerFormData,
): WsmDealerCustomerUpdateInput => ({
  group: data.groupId,
  taxExempt: data.taxExempt,
});

/** The group's own label: empty name means staff see the code (contract). */
export const getGroupLabel = (group: { code: string; name: string }) => group.name || group.code;
